import React from 'react';
import { Link } from 'react-router-dom';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import SEO, { createBreadcrumbSchema, createFAQSchema, softwareApplicationSchema } from '@/components/SEO';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ArrowRight, CheckCircle, Zap, CreditCard, Lock, Download, Sparkles, Gift } from 'lucide-react';

const FreeAIImageGenerator = () => {
  const faqs = [
    {
      question: "Is the AI image generator really free?",
      answer: "Yes! Our free tier gives you 5 AI-generated images every day, forever. No credit card required, no hidden fees. Just sign up with your email and start creating immediately."
    },
    {
      question: "Do I need to sign up to use the free generator?",
      answer: "You can try our generator without signing up! Guest users get 3 images to test. Sign up for free to get 5 images daily with full download rights."
    },
    {
      question: "What's the catch? Why is it free?",
      answer: "No catch! We offer a free tier because we believe everyone should have access to AI image generation. Our paid plans (starting at $7.99/month) offer more images and features for power users."
    },
    {
      question: "Can I use free images for commercial purposes?",
      answer: "Yes! All images you generate, even on the free tier, come with full commercial rights. Use them for your business, products, or marketing without restrictions."
    }
  ];

  const freeFeatures = [
    {
      icon: <Gift className="w-6 h-6" />,
      title: "5 Free Images Daily",
      description: "Generate 5 high-quality AI images every single day, forever free"
    },
    {
      icon: <CreditCard className="w-6 h-6" />,
      title: "No Credit Card Required",
      description: "Start creating immediately. We never ask for payment details for free tier"
    },
    {
      icon: <Lock className="w-6 h-6" />,
      title: "No Watermarks",
      description: "Your images are yours. No watermarks, no branding, pure clean output"
    },
    {
      icon: <Download className="w-6 h-6" />,
      title: "Full Download Rights",
      description: "Download your images in high resolution for any use, commercial included"
    },
    {
      icon: <Sparkles className="w-6 h-6" />,
      title: "FLUX AI Quality",
      description: "Access the same FLUX model quality that powers our premium plans"
    },
    {
      icon: <Zap className="w-6 h-6" />,
      title: "Fast Generation",
      description: "Get your AI images in seconds, not minutes. Same speed as paid plans"
    }
  ];

  const comparisonFeatures = [
    { feature: "Daily Images", free: "5 images", paid: "100-2000+ images" },
    { feature: "Image Quality", free: "✓ Full Quality", paid: "✓ Full Quality" },
    { feature: "Commercial Rights", free: "✓ Included", paid: "✓ Included" },
    { feature: "Watermarks", free: "✗ None", paid: "✗ None" },
    { feature: "Download Rights", free: "✓ Full", paid: "✓ Full" },
    { feature: "Bulk Generation", free: "1 at a time", paid: "Up to 10 at once" },
    { feature: "Queue Priority", free: "Standard", paid: "Priority" },
    { feature: "History Storage", free: "24 hours", paid: "Forever" },
  ];

  return (
    <>
      <SEO
        title="Free AI Image Generator | No Signup, No Credit Card, 5 Images Daily"
        description="Generate AI images 100% free. No signup required, no credit card, no watermarks. Create 5 stunning images daily with FLUX AI. The best free AI image generator online."
        keywords="free ai image generator, ai image generator free, free text to image, no signup image generator, free ai art generator, generate images free online, free ai image maker, no credit card ai generator"
        url="/free-ai-image-generator"
        jsonLd={[
          softwareApplicationSchema,
          createBreadcrumbSchema([
            { name: 'Home', url: '/' },
            { name: 'Free AI Image Generator', url: '/free-ai-image-generator' }
          ]),
          createFAQSchema(faqs)
        ]}
      />
      
      <Header />
      
      <main className="min-h-screen bg-background">
        {/* Hero Section */}
        <section className="relative py-20 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-green-500/10 via-emerald-500/5 to-background" />
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <Badge className="mb-4 bg-green-500/10 text-green-500 border-green-500/20">
                100% Free • No Credit Card
              </Badge>
              <h1 className="text-4xl md:text-6xl font-bold mb-6">
                Free AI Image Generator
                <span className="block text-green-500">Create Stunning Images Instantly</span>
              </h1>
              <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
                Generate 5 professional AI images daily, completely free. No signup required to try, 
                no credit card ever needed. Start creating in seconds.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button asChild size="lg" className="bg-green-500 hover:bg-green-600">
                  <Link to="/try-generator">
                    Start Creating Free <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                </Button>
                <Button asChild size="lg" variant="outline">
                  <Link to="/gallery">See Examples</Link>
                </Button>
              </div>
              
              {/* Trust Indicators */}
              <div className="mt-8 flex flex-wrap justify-center gap-6 text-sm text-muted-foreground">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>No signup required to try</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>No credit card ever</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>No watermarks</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* What's Included Free */}
        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-12">
              What You Get for Free
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
              {freeFeatures.map((feature, index) => (
                <Card key={index} className="border-green-500/10 hover:border-green-500/30 transition-colors">
                  <CardContent className="p-6">
                    <div className="w-12 h-12 rounded-lg bg-green-500/10 flex items-center justify-center text-green-500 mb-4">
                      {feature.icon}
                    </div>
                    <h3 className="text-lg font-semibold mb-2">{feature.title}</h3>
                    <p className="text-muted-foreground">{feature.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Free vs Paid Comparison */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-4">
              Free vs Paid Plans
            </h2>
            <p className="text-center text-muted-foreground mb-12">
              See what's included at every level
            </p>
            <div className="max-w-3xl mx-auto">
              <Card>
                <CardContent className="p-0">
                  <div className="grid grid-cols-3 gap-4 p-4 bg-muted/50 font-semibold border-b">
                    <div>Feature</div>
                    <div className="text-center text-green-500">Free</div>
                    <div className="text-center text-blue-500">Paid</div>
                  </div>
                  {comparisonFeatures.map((row, index) => (
                    <div key={index} className="grid grid-cols-3 gap-4 p-4 border-b last:border-b-0">
                      <div>{row.feature}</div>
                      <div className="text-center text-sm">{row.free}</div>
                      <div className="text-center text-sm">{row.paid}</div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="py-16 bg-muted/30">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-12">
              How to Use the Free Generator
            </h2>
            <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
              <div className="text-center">
                <div className="w-12 h-12 rounded-full bg-green-500 text-white flex items-center justify-center text-xl font-bold mx-auto mb-4">
                  1
                </div>
                <h3 className="font-semibold mb-2">Enter Your Prompt</h3>
                <p className="text-muted-foreground text-sm">
                  Describe the image you want. Be as detailed or simple as you like.
                </p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 rounded-full bg-green-500 text-white flex items-center justify-center text-xl font-bold mx-auto mb-4">
                  2
                </div>
                <h3 className="font-semibold mb-2">Generate with AI</h3>
                <p className="text-muted-foreground text-sm">
                  Click generate and watch as FLUX AI creates your image in seconds.
                </p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 rounded-full bg-green-500 text-white flex items-center justify-center text-xl font-bold mx-auto mb-4">
                  3
                </div>
                <h3 className="font-semibold mb-2">Download & Use</h3>
                <p className="text-muted-foreground text-sm">
                  Download your image and use it anywhere. Full commercial rights included.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-center mb-12">
              Frequently Asked Questions
            </h2>
            <div className="max-w-3xl mx-auto space-y-6">
              {faqs.map((faq, index) => (
                <Card key={index}>
                  <CardContent className="p-6">
                    <h3 className="font-semibold mb-2">{faq.question}</h3>
                    <p className="text-muted-foreground">{faq.answer}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-muted/30">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-3xl font-bold mb-6">
                Start Creating for Free Today
              </h2>
              <p className="text-xl text-muted-foreground mb-8">
                Join 50,000+ creators using our free AI image generator. 
                No risk, no commitment, just amazing AI-generated images.
              </p>
              <Button asChild size="lg" className="bg-green-500 hover:bg-green-600">
                <Link to="/try-generator">
                  Generate Images Free <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
              <p className="mt-4 text-sm text-muted-foreground">
                No signup required to try • No credit card ever
              </p>
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
    </>
  );
};

export default FreeAIImageGenerator;
